import { clsx } from 'clsx';
import isEmpty from 'lodash/isEmpty';
import { memo, PropsWithChildren, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ActionDot } from '~/components/ActionDot';
import IconButton from '~/components/buttons/NewIconButton';
import Icon from '~/components/data-display/Icon';
import { NewFeatureBadge } from '~/components/labels/NewFeatureBadge';
import { VStack } from '~/components/layout/Stack';
import Text from '~/components/typographies/Text';
import { useTrackNavigation } from '~/hooks/analytics/useTrackNavigation';
import IconClose from '~/icons/icon-close.svg?react';
import IconPadlockClosed from '~/icons/icon-padlock-closed-fill.svg?react';
import { useCloseSubmenu, useSetActiveSubmenuContent } from '~/state/navigationSidebar';
import { SubmenuSection } from '~/types/sidebar';
import { assertIsDefined } from '~/types/typeGuards';
import styles from './NavigationSidebarItem.module.scss';

interface NavigationSidebarItemProps {
  title: string;
  href?: string;
  iconComponent: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  submenuSections?: SubmenuSection[];
  disabled?: boolean;
  notificationCount?: number;
  isActive: boolean;
  isSubmenuOpen: boolean;
  onOpenSubmenu?: () => void;
  showNewFeatureBadge?: boolean;
}

interface SubmenuContentProps {
  title?: string;
}
export const SubmenuContentContainer: React.FC<PropsWithChildren<SubmenuContentProps>> = ({ children, title }) => {
  const { t } = useTranslation('sidebar');
  const closeSubmenu = useCloseSubmenu();
  return (
    <div className={styles.submenuContentContainer}>
      {title && (
        <div className={styles.submenuHeader}>
          <Text variant="body1" weight="semibold">
            {title}
          </Text>
          <IconButton Icon={IconClose} onClick={closeSubmenu} size="small" variant="ghost" label={t('close')} />
        </div>
      )}
      {children}
    </div>
  );
};

interface ItemProps {
  disabled?: boolean;
  href?: string;
  iconComponent: React.FC;
  isActive: boolean;
  isSubmenuOpen?: boolean;
  notificationCount?: number;
  onClick?: (event: React.MouseEvent, href?: string) => void;
  title: string;
  showNewFeatureBadge?: boolean;
}

const Item: React.FC<ItemProps> = ({
  disabled,
  href,
  iconComponent,
  isActive = false,
  isSubmenuOpen,
  notificationCount,
  onClick,
  title,
  showNewFeatureBadge,
}) => {
  const trackNavigation = useTrackNavigation();
  const computedContainerClassname = clsx(styles.container, {
    [styles.clickable!]: !!(href || onClick),
    [styles.disabled!]: disabled,
    [styles.active!]: isActive,
    [styles.submenuOpen!]: isSubmenuOpen,
  });

  const handleClick = (event: React.MouseEvent) => {
    if (href) {
      trackNavigation('sidebar', href);
    }
    onClick?.(event, href);
  };

  if (href) {
    return (
      <Link to={href} className={styles.link} onClick={handleClick}>
        <div className={computedContainerClassname}>
          {disabled && <Icon component={IconPadlockClosed} className={styles.disabledIcon} />}
          {!!notificationCount && <ActionDot className={styles.actionDot} />}
          <VStack justify="center" align="center" className={styles.iconContainer}>
            <Icon component={iconComponent} className={styles.icon} />
            {showNewFeatureBadge && <NewFeatureBadge size="small" className={styles.newFeatureBadge} />}
          </VStack>
          <Text variant={'tiny'} weight={'bold'} className={styles.title}>
            {title}
          </Text>
        </div>
      </Link>
    );
  }

  return (
    <div className={computedContainerClassname} onClick={handleClick}>
      {disabled && <Icon component={IconPadlockClosed} className={styles.disabledIcon} />}
      {!!notificationCount && <ActionDot className={styles.actionDot} />}
      <VStack justify="center" align="center" className={styles.iconContainer}>
        <Icon component={iconComponent} className={styles.icon} />
        {showNewFeatureBadge && <NewFeatureBadge size="small" className={styles.newFeatureBadge} />}
      </VStack>
      <Text variant={'tiny'} weight={'bold'} className={styles.title}>
        {title}
      </Text>
    </div>
  );
};

const NavigationSidebarItem = (props: NavigationSidebarItemProps) => {
  const {
    href,
    title,
    iconComponent,
    disabled = false,
    notificationCount,
    isActive,
    submenuSections,
    showNewFeatureBadge,
    onOpenSubmenu,
  } = props;
  const setActiveSubmenuContent = useSetActiveSubmenuContent();
  const closeSubmenu = useCloseSubmenu();

  const handleSubmenuClick = useCallback(
    (event: React.MouseEvent, href?: string) => {
      assertIsDefined(submenuSections, 'Submenu sections are required');
      setActiveSubmenuContent({ title, sections: submenuSections });
      onOpenSubmenu?.();
      if (href && location.pathname === href) {
        event.preventDefault();
      }
    },
    [onOpenSubmenu, setActiveSubmenuContent, submenuSections, title],
  );

  if (isEmpty(submenuSections) || !submenuSections) {
    return (
      <Item
        disabled={disabled}
        href={href}
        iconComponent={iconComponent}
        isActive={isActive}
        notificationCount={notificationCount}
        onClick={closeSubmenu}
        title={title}
        showNewFeatureBadge={showNewFeatureBadge}
      />
    );
  }

  return (
    <Item
      disabled={disabled}
      href={href}
      iconComponent={iconComponent}
      isActive={isActive}
      isSubmenuOpen={props.isSubmenuOpen}
      notificationCount={notificationCount}
      onClick={handleSubmenuClick}
      title={title}
      showNewFeatureBadge={showNewFeatureBadge}
    />
  );
};

export default memo(NavigationSidebarItem);
